import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Divider,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { format, parseISO, getYear, getMonth } from 'date-fns';
import axios from 'axios';
import { Release } from '@/types';

const DRAWER_WIDTH = 280;
const JIRA_BASE_URL = import.meta.env.VITE_JIRA_BASE_URL || 'https://upshop-company.atlassian.net';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// TMT/Upshop brand colors
const brandTeal = '#006261';
const brandTealDark = '#004D4C';
const brandTealLight = '#4DB6AC';
const sidebarBorder = '#007A79';
const background = '#F2F5F4';

const productSuiteImages: Record<string, string> = {
  'Fresh 360': '/images/green.png',
  'Compliance 360': '/images/red.png',
  'Waste 360': '/images/orange.png',
  'Commerce 360': '/images/purple.png',
  'Inventory 360': '/images/blue.png',
  'General': '/images/grey.png',
};

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Gated page shown when no valid token is present
function GatedPage() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: background,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 6,
          maxWidth: 500,
          textAlign: 'center',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: brandTeal,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 40, color: '#fff' }} />
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: brandTealDark }}>
          Upshop Release Notes
        </Typography>

        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.7 }}>
          Release notes are only accessible from within the Upshop platform.
          Please log in to your Upshop account and navigate to Release Notes from the main menu.
        </Typography>

        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3, mt: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Need help accessing release notes?
          </Typography>
          <Link
            href="https://support.upshop.com"
            target="_blank"
            rel="noopener"
            sx={{
              color: brandTeal,
              fontWeight: 500,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Contact Upshop Support
          </Link>
        </Box>
      </Paper>

      <Typography variant="caption" sx={{ mt: 4, color: 'text.disabled' }}>
        &copy; {new Date().getFullYear()} Upshop. All rights reserved.
      </Typography>
    </Box>
  );
}

export default function PublicReleases() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [selectedReleaseId, setSelectedReleaseId] = useState<string | null>(null);
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const [releases, setReleases] = useState<Release[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  // Get token from URL query parameter
  const token = searchParams.get('token');

  // Fetch releases with JWT authentication
  useEffect(() => {
    const fetchReleases = async () => {
      if (!token) {
        setIsLoading(false);
        setIsAuthorized(false);
        return;
      }

      try {
        const response = await axios.get<Release[]>(`${API_BASE_URL}/releases`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setReleases(response.data);
        setIsAuthorized(true);
      } catch (error) {
        console.error('Failed to fetch releases:', error);
        setIsAuthorized(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReleases();
  }, [token]);

  // Group releases by year and month
  const groupedReleases = useMemo(() => {
    if (!releases) return {};
    
    const grouped: Record<number, Record<number, Release[]>> = {};
    
    releases.forEach((release) => {
      const date = parseISO(release.releaseDate);
      const year = getYear(date);
      const month = getMonth(date);
      
      if (!grouped[year]) grouped[year] = {};
      if (!grouped[year][month]) grouped[year][month] = [];
      grouped[year][month].push(release);
    });

    // Sort releases within each month by date descending
    Object.keys(grouped).forEach((year) => {
      Object.keys(grouped[parseInt(year)]).forEach((month) => {
        grouped[parseInt(year)][parseInt(month)].sort(
          (a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
        );
      });
    });

    return grouped;
  }, [releases]);

  const years = Object.keys(groupedReleases).map(Number).sort((a, b) => b - a);

  // Select latest release by default or from URL
  useEffect(() => {
    if (releases && releases.length > 0) {
      if (id) {
        setSelectedReleaseId(id);
      } else {
        // Find the most recent release
        const sorted = [...releases].sort(
          (a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
        );
        setSelectedReleaseId(sorted[0].id);
        
        // Expand the year and month of the latest release
        const latestDate = parseISO(sorted[0].releaseDate);
        setExpandedYears(new Set([getYear(latestDate)]));
        setExpandedMonths(new Set([`${getYear(latestDate)}-${getMonth(latestDate)}`]));
      }
    }
  }, [releases, id]);

  const selectedRelease = releases?.find((r) => r.id === selectedReleaseId);

  const toggleYear = (year: number) => {
    const newExpanded = new Set(expandedYears);
    if (newExpanded.has(year)) {
      newExpanded.delete(year);
    } else {
      newExpanded.add(year);
    }
    setExpandedYears(newExpanded);
  };

  const toggleMonth = (yearMonth: string) => {
    const newExpanded = new Set(expandedMonths);
    if (newExpanded.has(yearMonth)) {
      newExpanded.delete(yearMonth);
    } else {
      newExpanded.add(yearMonth);
    }
    setExpandedMonths(newExpanded);
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), 'MMMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const getProductSuiteImage = (suite: string) => {
    return productSuiteImages[suite] || productSuiteImages['General'];
  };

  // Show loading spinner
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show gated page if not authorized
  if (!isAuthorized) {
    return <GatedPage />;
  }

  // Show releases content
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: background }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: brandTeal,
            color: '#fff',
          },
        }}
      >
        <Box sx={{ p: 2, backgroundColor: brandTealDark }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: brandTealLight }}>
            Upshop
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Release Notes
          </Typography>
        </Box>
        <Divider sx={{ borderColor: sidebarBorder }} />
        
        <List sx={{ flex: 1, overflow: 'auto' }}>
          {years.map((year) => (
            <Box key={year}>
              <ListItemButton 
                onClick={() => toggleYear(year)}
                sx={{ 
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                }}
              >
                <ListItemText primary={year} />
                {expandedYears.has(year) ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={expandedYears.has(year)} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {Object.keys(groupedReleases[year] || {})
                    .map(Number)
                    .sort((a, b) => b - a)
                    .map((month) => {
                      const yearMonth = `${year}-${month}`;
                      return (
                        <Box key={yearMonth}>
                          <ListItemButton 
                            sx={{ 
                              pl: 4,
                              '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                            }} 
                            onClick={() => toggleMonth(yearMonth)}
                          >
                            <ListItemText primary={monthNames[month]} />
                            {expandedMonths.has(yearMonth) ? <ExpandLess /> : <ExpandMore />}
                          </ListItemButton>
                          <Collapse in={expandedMonths.has(yearMonth)} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                              {groupedReleases[year][month].map((release) => (
                                <ListItemButton
                                  key={release.id}
                                  sx={{
                                    pl: 6,
                                    borderLeft: selectedReleaseId === release.id 
                                      ? `3px solid ${brandTealLight}` 
                                      : '3px solid transparent',
                                    backgroundColor: selectedReleaseId === release.id 
                                      ? 'rgba(255, 255, 255, 0.18)' 
                                      : 'transparent',
                                    '&:hover': { 
                                      backgroundColor: 'rgba(255,255,255,0.08)',
                                      borderLeftColor: brandTealLight,
                                    },
                                  }}
                                  onClick={() => setSelectedReleaseId(release.id)}
                                >
                                  <ListItemText
                                    primary={
                                      <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                                        {release.title}
                                      </Typography>
                                    }
                                    secondary={
                                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                                        {formatDate(release.releaseDate)}
                                      </Typography>
                                    }
                                  />
                                </ListItemButton>
                              ))}
                            </List>
                          </Collapse>
                        </Box>
                      );
                    })}
                </List>
              </Collapse>
            </Box>
          ))}
        </List>

        <Divider sx={{ borderColor: sidebarBorder }} />
        <Box sx={{ p: 2 }}>
          <Link
            href="https://support.upshop.com"
            target="_blank"
            rel="noopener"
            sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.75rem', textDecoration: 'none' }}
          >
            Need help? Visit Support
          </Link>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 4, maxWidth: 900, mx: 'auto', bgcolor: '#FFFFFF' }}>
        {selectedRelease ? (
          <>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#1E2A3B' }}>
              {selectedRelease.title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
              Released on {formatDate(selectedRelease.releaseDate)}
            </Typography>

            {selectedRelease.description && (
              <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedRelease.description}
                </Typography>
              </Paper>
            )}

            {Array.isArray(selectedRelease.itemGroups) && selectedRelease.itemGroups.map((group) => {
              // Group items by module within each product suite
              const itemsByModule = (Array.isArray(group.items) ? group.items : []).reduce((acc, item) => {
                const mod = item.module || 'General';
                if (!acc[mod]) acc[mod] = [];
                acc[mod].push(item);
                return acc;
              }, {} as Record<string, typeof group.items>);

              return (
                <Paper key={group.productSuite} sx={{ p: 3, mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <img
                      src={getProductSuiteImage(group.productSuite)}
                      alt={group.productSuite}
                      style={{ width: 40, height: 40 }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <Typography variant="h5" sx={{ fontWeight: 600, color: brandTeal }}>
                      {group.productSuite}
                    </Typography>
                  </Box>

                  {Object.entries(itemsByModule).map(([moduleName, items]) => (
                    <Box key={moduleName} sx={{ mb: 3, ml: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        {moduleName}
                      </Typography>
                      {items.map((item, idx) => (
                        <Box key={item.itemId || idx} sx={{ mb: 2, ml: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Link
                              href={`${JIRA_BASE_URL}/browse/${item.issueKey}`}
                              target="_blank"
                              rel="noopener"
                              sx={{ 
                                fontWeight: 600, 
                                color: brandTeal,
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {item.issueKey.includes('-') ? item.issueKey.split('-')[1] : item.issueKey}
                            </Link>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              - {item.summary}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: 'text.primary', ml: 0 }}>
                            {item.content}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Paper>
              );
            })}
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No releases found. Check back soon!
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
